/**
 * Finding a product by typing at it.
 *
 * The old tree answered this with a `products.search` tRPC procedure and a
 * `LIKE` — a round trip per keystroke, against a database that was on the same
 * machine then and is not now. Here the whole catalogue is already in the page
 * (it has to be: `docs/offline.md` requires a kiosk that sells with the uplink
 * pulled), so search is a pure function over an array and works offline for
 * free.
 *
 * Three fields are searched and they are not equivalent. The name the customer
 * is reading — `name`, or `nameEn` when the kiosk is in English — is the one
 * that ranks; the other name is an alias; the SKU is what a member of staff
 * reads off a label. Ranking keeps them in that order rather than treating a
 * SKU substring as being as good as a name.
 *
 * **A query is words, not a string.** It is split on whitespace and every token
 * has to match, because a customer narrowing a menu types "матча латте" and a
 * single-needle matcher answers that with nothing at all — the word order in a
 * product name is not something anybody can be expected to guess.
 */
import { displayName, type Locale, type Product, type Sku } from './catalogue'

/**
 * Latin letters that are drawn as Cyrillic ones.
 *
 * This is not a transliteration table and must never grow into one. It is the
 * repair for a specific data defect: a 1C operator types a product name with
 * the layout half switched and `Американо` arrives with a Latin `a` (U+0061) in
 * the middle of it. The kiosk keyboard cannot type that character, so the
 * product becomes unreachable by name — and the same wrong code point also
 * breaks `localeCompare`, the receipt and console search.
 *
 * Lowercase carries only the pairs that are genuinely confusable in lowercase
 * form. `b`, `h`, `k`, `m` and `t` are confusable as capitals and not as
 * minuscules, so a lowercase `t` inside a Cyrillic word stays a miss rather
 * than silently becoming `т`.
 */
const HOMOGLYPH: Record<string, string> = {
  A: 'А',
  B: 'В',
  C: 'С',
  E: 'Е',
  H: 'Н',
  K: 'К',
  M: 'М',
  O: 'О',
  P: 'Р',
  T: 'Т',
  X: 'Х',
  Y: 'У',
  a: 'а',
  c: 'с',
  e: 'е',
  o: 'о',
  p: 'р',
  x: 'х',
  y: 'у'
}

const CYRILLIC = /[Ѐ-ӿ]/
const LATIN = /[A-Za-z]/

/**
 * Repair one word, and only if it is obviously broken.
 *
 * The rule is narrow on purpose, because the wide version is wrong: folding
 * Latin and Cyrillic together everywhere would make "Сола" match "Cola" and
 * collide two genuinely different products. So a word is repaired only when it
 * is *already majority Cyrillic* — which is to say, when the Latin letters in
 * it are the defect rather than the word. An all-Latin word ("Cola", "latte",
 * "CF-LATTE") is never touched, in either direction, and neither is an
 * all-Cyrillic one.
 *
 * Per word rather than per field, or "Хүйтэн Cola" would count six Cyrillic
 * against four Latin and quietly turn a real English name into a Cyrillic one.
 */
function repairWord(word: string): string {
  let cyrillic = 0
  let latin = 0
  for (const character of word) {
    if (CYRILLIC.test(character)) cyrillic++
    else if (LATIN.test(character)) latin++
  }
  if (latin === 0 || cyrillic <= latin) return word

  let out = ''
  for (const character of word) out += HOMOGLYPH[character] ?? character
  return out
}

/**
 * Casefold for comparison.
 *
 * `toLowerCase` is correct for Cyrillic here — Ө/Ү fold to ө/ү. The script
 * repair runs first, on the original casing, because the homoglyph table is
 * keyed by the letter as it was typed.
 */
const fold = (value: string): string => value.trim().replace(/\S+/g, repairWord).toLowerCase()

/** Enough words to narrow a menu. Past this a paste is being pricing-scanned, not searched. */
const MAX_TOKENS = 6

const tokenise = (query: string): readonly string[] => {
  const folded = fold(query)
  if (folded.length === 0) return []
  return folded.split(/\s+/).slice(0, MAX_TOKENS)
}

/**
 * Lower is better. Ordered by how sure we are the customer meant it.
 *
 * A plain object rather than an `enum`: `isolatedModules` is on across this
 * workspace and a `const enum` cannot be erased by a single-file transpiler.
 */
const Rank = {
  Sku: 0,
  NamePrefix: 1,
  AliasPrefix: 2,
  NameWordPrefix: 3,
  NameSubstring: 4,
  AliasSubstring: 5,
  Miss: 99
} as const

type Rank = (typeof Rank)[keyof typeof Rank]

/** Does `haystack` have `needle` at the start of any word? */
function hasWordPrefix(haystack: string, needle: string): boolean {
  let from = 0
  for (;;) {
    const at = haystack.indexOf(needle, from)
    if (at === -1) return false
    if (at === 0 || haystack[at - 1] === ' ' || haystack[at - 1] === '-') return true
    from = at + 1
  }
}

/**
 * A product's searchable text, folded once.
 *
 * Once per product per *query*, not once per token: `rankOf` used to fold five
 * strings on every call, so a two-word query doubled the allocations across the
 * whole catalogue on every keystroke.
 */
interface Folded {
  readonly name: string
  readonly aliases: readonly string[]
  readonly sku: string
  readonly id: string
}

function foldProduct(product: Product, locale: Locale): Folded {
  // The name that ranks is the name the customer can read. Under `en` that is
  // `nameEn`, and the Mongolian name drops to being an alias — otherwise a
  // tourist's exact match on the tile in front of them ranks below somebody
  // else's mid-word substring.
  const primary = displayName(product, locale)
  const others = locale === 'en' ? [product.name] : [product.nameEn]
  return {
    name: fold(primary),
    aliases: others.filter((a): a is string => a !== undefined && a !== primary).map(fold),
    sku: fold(product.sku),
    id: fold(product.id)
  }
}

function rankOf(folded: Folded, needle: string): Rank {
  if (folded.sku === needle || folded.id === needle) return Rank.Sku
  if (folded.name.startsWith(needle)) return Rank.NamePrefix
  if (folded.aliases.some((a) => a.startsWith(needle))) return Rank.AliasPrefix
  if (hasWordPrefix(folded.name, needle)) return Rank.NameWordPrefix
  if (folded.name.includes(needle)) return Rank.NameSubstring
  if (folded.aliases.some((a) => a.includes(needle))) return Rank.AliasSubstring
  return Rank.Miss
}

export interface SearchOptions {
  /** Stop after this many. A kiosk grid shows a screenful, not a catalogue. */
  readonly limit?: number
  /** Which name is *the* name. Defaults to `mn`, the kiosk's default language. */
  readonly locale?: Locale
}

export interface SearchOutcome {
  /** Best first, capped by `options.limit`. */
  readonly hits: readonly Product[]
  /**
   * How many matched before the cap.
   *
   * The screen showed `hits.length` and called it the number of matches, so a
   * query that hit 214 products reported 60 — the limit, presented as a fact
   * about the menu.
   */
  readonly total: number
}

/**
 * Products matching `query`, best first, with an honest count.
 *
 * An empty or whitespace-only query returns nothing rather than everything:
 * the caller shows the category grid in that case, and returning the whole
 * catalogue would make "cleared the box" render as "searched for everything".
 *
 * Every token must match — a product that answers "матча" but not "латте" is
 * not what was asked for. The product's rank is the **worst** of its token
 * ranks, with their sum as the tie-break: worst-wins is monotone, so an extra
 * word can only ever narrow, and for a single token it collapses to exactly the
 * ranking this function has always had.
 *
 * Disabled products are excluded — a search that surfaces something the menu
 * will not let you tap is a search that produces a support call.
 */
export function searchMenu(products: readonly Product[], query: string, options: SearchOptions = {}): SearchOutcome {
  const tokens = tokenise(query)
  if (tokens.length === 0) return { hits: [], total: 0 }

  const locale = options.locale ?? 'mn'
  const hits: { product: Product; sortKey: string; worst: number; sum: number }[] = []

  for (const product of products) {
    if (!product.enabled) continue
    const folded = foldProduct(product, locale)

    let worst: number = Rank.Sku
    let sum = 0
    for (const token of tokens) {
      const rank = rankOf(folded, token)
      if (rank === Rank.Miss) {
        worst = Rank.Miss
        break
      }
      if (rank > worst) worst = rank
      sum += rank
    }
    if (worst === Rank.Miss) continue

    hits.push({ product, sortKey: displayName(product, locale), worst, sum })
  }

  hits.sort((a, b) => a.worst - b.worst || a.sum - b.sum || a.sortKey.localeCompare(b.sortKey, 'mn'))

  const limit = options.limit ?? hits.length
  return { hits: hits.slice(0, limit).map((h) => h.product), total: hits.length }
}

/** The hits alone, for the callers that do not need the count. */
export const searchProducts = (
  products: readonly Product[],
  query: string,
  options: SearchOptions = {}
): readonly Product[] => searchMenu(products, query, options).hits

/** The SKUs a query matches. Handy where only identity is needed. */
export const searchSkus = (products: readonly Product[], query: string): readonly Sku[] =>
  searchProducts(products, query).map((p) => p.id)
